import { selectSiteHasFeature } from "../../featureFlags/SitesFeatureFlagsStore";
import React, { lazy, Suspense } from "react";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

import classes from "./Header.module.css";
import SubscriptionNotice from "../../subscriptions/SubscriptionNotice";
import SystemStatus from "./SystemStatus";
import getIcons from "../../../SolarGikLib/icons/Icons";
import { GeneralIcons, IconCategory } from "../../../SolarGikLib/icons/IconsModels";
import { RootState } from "../../app/Store";
import Emergency from "../../banner/emergency/Emergency";
import TimeComponent from "../../banner/TimeComponent";
import UserOptionDropdownMenu from "../../banner/UserOptionDropdownMenu";
import InteractiveSolargikLogo from "../InteractiveSolargikLogo";
import SwitchSiteComponent from "../SwitchSiteComponent";
import ExistsScheduledCommandPopOver from "../../trackers/ScheduleCommand/ExistsScheduledCommandPopOver";
import { FeatureFlag } from "../../featureFlags/FeatureFlagsModels";
import DiffuseOptimizationStatus from "../../diffuseOptimization/DiffuseOptimizationStatus";
import { isMonitoringOnlySite } from "../../purchasedProducts/MonitorOnlySelectors";
import { selectHasTrackers } from "../../sites/SiteStore";
import { UserAccessType } from "../../user/Models";
import { SitePage } from "../../../pages/PageNames";
import { ConfigurationIcon } from "../../../SolarGikLib/icons/common/navigation/NavIcons";

import type { SiteGroupNavigation } from "../Layout";

const ChatWidget = lazy(() =>
  import("../../chat/components/ChatWidget").then((module) => ({ default: module.ChatWidget }))
);

interface IHeaderProps {
  siteGroup?: SiteGroupNavigation;
  isAiChatEnabled: boolean;
}

export const Header: React.FC<IHeaderProps> = ({ isAiChatEnabled, siteGroup }) => {
  const siteId = useSelector((state: RootState) => state.site.siteId);
  const isScheduledMaintenanceEnabled = useSelector((state: RootState) =>
    selectSiteHasFeature(state, siteId, FeatureFlag.ScheduledMaintenance)
  );
  const currentSiteCode = useSelector(
    (state: RootState) => state.multiSitesMetadata.sites[siteId]?.siteCode
  );
  const canViewConfiguration = useSelector(
    (state: RootState) => state.user.userType >= UserAccessType.Engineer
  );
  const isMonitoringOnly = useSelector((state: RootState) => isMonitoringOnlySite(state, siteId));
  const siteHasTrackers = useSelector(selectHasTrackers);
  const canUseTrackerControls = !siteGroup && siteHasTrackers && !isMonitoringOnly;

  const UserIcon = getIcons(GeneralIcons.UserIcon, IconCategory.General);
  return (
    <AppBar position="relative" className={classes["app-bar"]}>
      <Toolbar className={classes["tool-bar"]}>
        <div className={`${classes.container} ${classes["left-components"]}`}>
          <div className={`${classes.container} ${classes["logo"]}`}>
            <InteractiveSolargikLogo />
          </div>
          <div className={classes["sites-dropdown"]}>
            <SwitchSiteComponent siteGroup={siteGroup} />
          </div>
          {!siteGroup && <SubscriptionNotice key={siteId} />}
          {!siteGroup && canViewConfiguration && currentSiteCode && (
            <Tooltip title="Configuration">
              <IconButton
                component={NavLink}
                to={`/${encodeURI(currentSiteCode)}/${SitePage.Configuration}`}
                className={classes["configuration-navigation"]}
                aria-label="Configuration"
              >
                <ConfigurationIcon backgroundColor="transparent" strokeColor="white" className="" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        <div className={`${classes.container} ${classes["right-components"]}`}>
          {!siteGroup && (
            <>
              <div className={classes["diffuse-optimization"]}>
                <DiffuseOptimizationStatus siteId={siteId} />
              </div>
              <div className={`${classes.container} ${classes["system-status"]}`}>
                <SystemStatus
                  siteId={siteId}
                  canControlTrackers={canUseTrackerControls}
                  hasTrackers={siteHasTrackers}
                />
              </div>
            </>
          )}
          {canUseTrackerControls && (
            <div className={classes["emergency"]}>
              <Emergency />
            </div>
          )}
          {isAiChatEnabled && (
            <div className={classes["chat-widget"]}>
              <Suspense fallback={null}>
                <ChatWidget />
              </Suspense>
            </div>
          )}
          {isScheduledMaintenanceEnabled && canUseTrackerControls && (
            <ExistsScheduledCommandPopOver siteId={siteId} />
          )}
          <div className={`${classes.container} ${classes["time-and-user"]}`}>
            <TimeComponent timeZone={siteGroup?.timeZone ?? undefined} />
            <UserOptionDropdownMenu icon={UserIcon} addSingleSiteMenuItems={!siteGroup} />
          </div>
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
